import React, { useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useState } from 'react';

declare global {
  interface Window {
    forceRefreshAvatar?: (canvasId?: string) => Promise<boolean>;
    clearAvatarContent?: (canvasId?: string) => boolean | void;
    __live2dAvatarControllers__?: Map<string, { refresh: () => Promise<boolean>; clear: () => boolean | void }>;
  }
}

interface Live2DAvatarGeneratedProps {
  className?: string;
  scale?: number;
  position?: { x: number; y: number };
  onLoad?: () => void;
  onError?: (error: string) => void;
  jsFile?: string; // 指定要加载的JS文件
  textureUrl?: string; // 覆盖纹理的远程地址
  modelUrl?: string; // 指定自定义模型JSON
}

export interface Live2DAvatarGeneratedRef {
  refreshModel: () => Promise<void>;
}

const Live2DAvatarGenerated = forwardRef<Live2DAvatarGeneratedRef, Live2DAvatarGeneratedProps>(({
  className = '',
  scale = 0.4,
  position = { x: 0, y: -50 },
  onLoad,
  onError,
  jsFile = '/view1/anime_haru_generated.js',
  textureUrl,
  modelUrl
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasId] = useState(() => `live2d-canvas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const scriptLoadPromiseRef = useRef<Promise<void> | null>(null);

  // 动态加载脚本的函数（为每个实例创建独立脚本）
  const loadScript = useCallback((url: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      // 为每个实例创建独立的脚本ID
      const scriptId = `avatar-anime-generated-script-${canvasId}`;
      const existingScript = document.getElementById(scriptId);

      if (existingScript) {
        console.log(`Removing existing script before loading new one: ${scriptId}`);
        existingScript.remove();
      }

      // Canvas ID现在由JS脚本自动检测，无需设置全局变量

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = url;
      script.async = false;
      script.dataset.live2dCanvasId = canvasId;
      if (typeof window !== 'undefined') {
        try {
          const parsedUrl = new URL(url, window.location.origin);
          const loadToken = parsedUrl.searchParams.get('t') || parsedUrl.searchParams.get('_refresh');
          if (loadToken) {
            script.dataset.live2dToken = loadToken;
          }
        } catch (error) {
          console.warn('Failed to parse Live2D script URL:', error);
        }
      }
      script.onload = () => {
        console.log(`Script loaded successfully for canvas: ${canvasId}`);
        resolve();
      };
      script.onerror = (error) => {
        console.error(`Script load failed: ${scriptId}`, error);
        reject(error);
      };
      document.head.appendChild(script);
    });
  }, [canvasId]);

  const buildScriptUrl = useCallback((forceRefresh = false) => {
    if (typeof window === 'undefined') {
      return jsFile;
    }

    const urlObject = new URL(jsFile, window.location.origin);
    if (textureUrl) {
      urlObject.searchParams.set('textureUrl', textureUrl);
    } else {
      urlObject.searchParams.delete('textureUrl');
    }
    if (modelUrl) {
      urlObject.searchParams.set('modelUrl', modelUrl);
    } else {
      urlObject.searchParams.delete('modelUrl');
    }

    if (forceRefresh) {
      urlObject.searchParams.set('_refresh', Date.now().toString());
    } else {
      urlObject.searchParams.delete('_refresh');
    }

    return urlObject.toString();
  }, [jsFile, textureUrl, modelUrl]);

  // 按顺序加载所需的脚本（为每个实例创建独立脚本）
  const loadDependencies = useCallback(async (forceRefresh = false) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (scriptLoadPromiseRef.current) {
      try {
        await scriptLoadPromiseRef.current;
      } catch (_) {
        // ignore previous load error; we'll attempt a fresh load below
      }
      scriptLoadPromiseRef.current = null;
    }

    const baseUrl = buildScriptUrl(forceRefresh);
    const urlObject = new URL(baseUrl, window.location.origin);
    urlObject.searchParams.set('instance', canvasId);
    urlObject.searchParams.set('t', Date.now().toString());
    const finalUrl = urlObject.toString();

    console.log(`Loading script for canvas: ${canvasId}, URL: ${finalUrl}`);

    // 在加载脚本前，设置全局变量来指定当前实例的canvas ID
    (window as any).currentCanvasId = canvasId;
    
    const loadPromise = loadScript(finalUrl);
    scriptLoadPromiseRef.current = loadPromise.finally(() => {
      if (scriptLoadPromiseRef.current === loadPromise) {
        scriptLoadPromiseRef.current = null;
      }
    });
    await loadPromise;
  }, [buildScriptUrl, loadScript, canvasId]);

  // 刷新模型的方法
  const refreshModel = useCallback(async () => {
    try {
      console.log('Refreshing model...');
      
      // 检查canvas元素是否存在
      if (!canvasRef.current) {
        console.error('Canvas reference is not available');
        onError?.('Canvas element not found');
        return;
      }
      
      // 检查forceRefreshAvatar函数是否存在
      const registry = window.__live2dAvatarControllers__;
      const controllerReady = registry?.has(canvasId);
      if (!controllerReady) {
        console.log('Avatar controller not ready yet, skipping refresh for now.');
        return;
      }

      if (typeof window.forceRefreshAvatar === 'function') {
        console.log('Using forceRefreshAvatar...');
        const success = await window.forceRefreshAvatar(canvasId);
        if (success) {
          console.log('Model refresh completed via forceRefreshAvatar');
          return;
        } else {
          console.log('forceRefreshAvatar failed, falling back to script reload');
        }
      }
      
      // 回退方案：重新加载脚本
      console.log('Falling back to script reload...');
      
      if (typeof window.clearAvatarContent === 'function') {
        try {
          window.clearAvatarContent(canvasId);
        } catch (clearError) {
          console.warn('Failed to clear avatar content before reload:', clearError);
        }
      }

      // 首先移除现有的脚本标签以确保重新加载
      await loadDependencies(true);
      console.log('Model refresh completed via script reload');
    } catch (error) {
      console.error('Failed to refresh model:', error);
      const errorMsg = 'モデルの更新に失敗しました';
      onError?.(errorMsg);
    }
  }, [canvasId, loadDependencies, onError]);

  // 暴露刷新方法给父组件
  useImperativeHandle(ref, () => ({
    refreshModel
  }));

  useEffect(() => {
    const cleanup = () => {
      const scriptId = `avatar-anime-generated-script-${canvasId}`;
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
      if (window.__live2dAvatarControllers__) {
        window.__live2dAvatarControllers__.delete(canvasId);
      }
    };

    // 确保canvas元素已经挂载后再加载脚本
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('Canvas ref not ready, waiting...');
      // 使用setTimeout确保在下一个事件循环中再检查
      const timer = setTimeout(() => {
        // 为view2实例添加延迟，避免WebGL上下文冲突
        const delay = jsFile.includes('view2') ? 2000 : 0;
        setTimeout(() => {
          loadDependencies()
            .then(() => {
              console.log('anime_haru_generated.js loaded successfully');
              onLoad?.();
            })
            .catch((error) => {
              console.error('Failed to load anime_haru_generated.js:', error);
              onError?.(error.message || 'Failed to load Live2D model');
            });
        }, delay);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        cleanup();
      };
    }

    // canvas已经准备好，为view2实例添加延迟
    const delay = jsFile.includes('view2') ? 2000 : 0;
    const loadHandle = setTimeout(() => {
      loadDependencies()
        .then(() => {
          console.log('anime_haru_generated.js loaded successfully');
          onLoad?.();
        })
        .catch((error) => {
          console.error('Failed to load anime_haru_generated.js:', error);
          onError?.(error.message || 'Failed to load Live2D model');
        });
    }, delay);

    return () => {
      clearTimeout(loadHandle);
      if (typeof window.clearAvatarContent === 'function') {
        try {
          window.clearAvatarContent(canvasId);
        } catch (clearError) {
          console.warn('Failed to clear avatar content during cleanup:', clearError);
        }
      }
      cleanup();
    };
  }, [canvasId, loadDependencies, onLoad, onError, jsFile]);

  return (
    <div className={`live2d-container ${className}`}>
      <canvas 
        ref={canvasRef}
        id={canvasId}
        className="live2d-canvas"
        style={{ 
          width: '100%', 
          height: '100%',
          display: 'block'
        }}
      />
    </div>
  );
});

Live2DAvatarGenerated.displayName = 'Live2DAvatarGenerated';

export default Live2DAvatarGenerated;
